/**
 * `/api/saved-views[/:id]` (Requirement 11 criteria 10 to 16).
 *
 * A Saved_View stores the same filter representation the screen and the export use,
 * which is why it holds an {@link initiativeFilterSchema} value rather than a
 * private encoding: a view, the calendar query it reproduces and the export of that
 * view are then the same grammar by construction (R13.6).
 *
 * Rendering a shared view evaluates the *requesting* user's permissions (R11.12),
 * so a stored view carries no scope of its own. Nothing here needs a `version`
 * either: R11.14 restricts who may edit a shared view, and a Saved_View has no
 * concurrent-edit story to lose.
 *
 * Stale stored values are reported, not rejected: a deactivated value is still
 * applied and named (R11.15), and a value that no longer exists is dropped with a
 * count of omissions (R11.16). Both live on the read shape, because they are facts
 * about the load rather than about the stored view.
 */
import { z } from 'zod';
import { calendarOrientationSchema } from './enums.js';
// The screen list lives with the other governance enums, built from one frozen tuple
// that matches migration 27's `saved_view_screen_known` check - so a value this schema
// accepts is a value the column accepts.
import { savedViewScreenSchema } from './governance-enums.js';
import { initiativeFilterSchema, sortOrderSchema } from './filters.js';
import { SAVED_VIEW_NAME_MAX_LENGTH, SAVED_VIEW_NAME_MIN_LENGTH, } from './limits.js';
import { idSchema, textSchema, timestampSchema } from './primitives.js';
/** `/api/saved-views/:id`. */
export const savedViewIdParamsSchema = z.object({ id: idSchema });
/** A filter value that no longer applies cleanly, named for the user (R11.15). */
export const staleFilterValueSchema = z.object({
    dimension: z.string().min(1),
    valueId: idSchema,
    name: z.string().min(1),
});
/** The stored parts of a Saved_View (R11.10). */
export const savedViewWriteSchema = z.object({
    name: textSchema(SAVED_VIEW_NAME_MIN_LENGTH, SAVED_VIEW_NAME_MAX_LENGTH),
    filters: initiativeFilterSchema,
    orientation: calendarOrientationSchema,
    sort: sortOrderSchema,
    /** R11.12: publishing makes the view selectable by every authenticated user. */
    isShared: z.boolean().default(false),
    /**
     * The screen this view belongs to (`complan-governance` R6.10).
     *
     * Defaulted to `'calendar'` for the same reason migration 0027 defaults the column:
     * every view stored before the screen existed was saved from the Calendar_View, so
     * that is what those rows are, and an older client that sends no screen is saving
     * from there too.
     */
    screen: savedViewScreenSchema.default('calendar'),
});
/** `POST /api/saved-views`. The per-user maximum is configuration, checked server-side (R11.10). */
export const createSavedViewRequestSchema = savedViewWriteSchema;
/** `PUT /api/saved-views/:id`. Owner and Commercial_Planning only (R11.14). */
export const updateSavedViewRequestSchema = savedViewWriteSchema;
/** A Saved_View as read back. */
export const savedViewSchema = savedViewWriteSchema.extend({
    id: idSchema,
    ownerUserId: idSchema,
    createdAt: timestampSchema,
    /** Whether the requesting user may edit, rename, unpublish or delete it (R11.14). */
    editableByRequester: z.boolean(),
    /** Stored values that have been deactivated since; still applied, and named (R11.15). */
    deactivatedFilterValues: z.array(staleFilterValueSchema).default([]),
    /** How many stored values no longer exist and were dropped from evaluation (R11.16). */
    omittedFilterValueCount: z.number().int().min(0).default(0),
});
/**
 * `GET /api/saved-views`. The requesting user's own views plus the shared ones.
 *
 * `ownedCount` is here because R11.10 caps ownership: the client needs to know it
 * has reached the limit before it offers to save another.
 */
export const savedViewListResponseSchema = z.object({
    items: z.array(savedViewSchema).default([]),
    ownedCount: z.number().int().min(0),
    maxOwnedPerUser: z.number().int().min(1),
});
/** `DELETE /api/saved-views/:id`. */
export const deleteSavedViewResponseSchema = z.object({
    id: idSchema,
    deleted: z.literal(true),
});
//# sourceMappingURL=saved-view.js.map